/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 *  Based on https://github.com/floydjones1/react-lazy-retry/blob/main/src/index.ts
 */
import {ComponentType, lazy} from 'react';

type ComponentPromise<T = any> = Promise<{default: ComponentType<T>}>;

const MAX_RETRIES = 5;
const RETRY_INTERVAL = 1000;

/**
 * A wrapper for React.lazy which retries the import for a limited number of times on chunk load error
 * @param {ImportComponent} component A function supported by React.lazy (expected to be a dynamic import)
 * @return {function} React.lazy function
 */
export default function lazyRetry(
  component: () => ComponentPromise,
  retries?: number,
  interval?: number,
): React.LazyExoticComponent<React.ComponentType<any>> {
  return lazy(() => retry(component, retries, interval));
}

/**
 * Execute a Promise-returning function after a set delay, with an iteration countdown
 * @param {ImportComponent} fn A Promise-returning function
 * @param {number} retries The maximum number of times to retry
 * @param {number} interval The base amount of time to wait between retries
 * @return {Promise}
 */
function retry(
  fn: () => ComponentPromise,
  retriesLeft = MAX_RETRIES,
  interval = RETRY_INTERVAL,
): ComponentPromise {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch(error => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            reject(error);
            return;
          }
          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
}
